import { z } from 'zod';

/**
 * A pragmatic, OCI-reference-shaped syntax check (research.md D5) — not the
 * full distribution-spec grammar, which no part of the backend publishes or
 * enforces either. Catches the obviously-wrong cases (empty string, no
 * repository path, stray whitespace); the server remains the final authority
 * (FR-007a).
 */
const IMAGE_REFERENCE_PATTERN =
  /^(?:(?:[\w.-]+\.[\w.-]+|localhost)(?::\d+)?\/)?[a-z0-9]+(?:[._-][a-z0-9]+)*(?:\/[a-z0-9]+(?:[._-][a-z0-9]+)*)*(?::[\w][\w.-]{0,127}|@sha256:[a-f0-9]{64})?$/;

function isPlausibleImageReference(value: string): boolean {
  return IMAGE_REFERENCE_PATTERN.test(value);
}

export const imageReferenceSchema = z
  .string()
  .trim()
  .min(1, 'Image reference is required.')
  .refine(
    isPlausibleImageReference,
    'Enter a valid image reference, e.g. registry.example.com/team/image:tag.',
  );

/**
 * The add-image form's edited shape — `label` is required here even though
 * `ExecutionImageCreate.label` is a plain (non-optional) string on the wire,
 * so nothing about this type loosens that.
 */
export interface AddExecutionImageFormValues {
  connectionId: string;
  imageReference: string;
  label: string;
}

/**
 * Validates the add-image form's full state, run via `zodResolver` — same
 * convention `connectionFormSchema` establishes in the `connections`
 * package.
 */
export const addExecutionImageFormSchema = z.object({
  connectionId: z.string().min(1, 'Choose a connection.'),
  imageReference: imageReferenceSchema,
  label: z.string().trim().min(1, 'Label is required.'),
}) satisfies z.ZodType<AddExecutionImageFormValues>;

export const ADD_EXECUTION_IMAGE_DEFAULT_VALUES: AddExecutionImageFormValues = {
  connectionId: '',
  imageReference: '',
  label: '',
};
